import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import "./ClientSelectModal.css";
import { API_URL } from "../../config/api";

const PAGE_SIZE = 5;

const typeMap = {
  individual: "ИП",
  llp: "ТОО",
  jsc: "АО",
  fl: "ФЛ",
};

const ClientSelectModal = ({ open, onClose, onSelectClient }) => {
  const [activeTab, setActiveTab] = useState("list"); // list | new

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [page, setPage] = useState(1);

  const [clients, setClients] = useState([]);
  const [lastPage, setLastPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const [selectedId, setSelectedId] = useState(null);

  // form: new client
  const [form, setForm] = useState({
    sn: "",
    type: "client",
    org_type: "individual",
    name: "",
    phone: "",
    city: "",
    address: "",
  });

  const token = localStorage.getItem("token");

  const selectedClient = useMemo(() => {
    return clients.find((c) => c.id === selectedId) || null;
  }, [clients, selectedId]);

  const pageNumbers = useMemo(() => {
    const max = Math.min(lastPage, 7);
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [lastPage]);

  // reset when modal opens
  useEffect(() => {
    if (!open) return;

    setActiveTab("list");
    setQuery("");
    setDebouncedQuery("");
    setPage(1);
    setSelectedId(null);

    setClients([]);
    setLastPage(1);

    setError("");
    setCreating(false);

    setForm({
      sn: "",
      type: "client",
      org_type: "individual",
      name: "",
      phone: "",
      city: "",
      address: "",
    });
  }, [open]);

  // debounce query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  // if search changed -> go first page
  useEffect(() => {
    if (!open) return;
    if (activeTab !== "list") return;
    setPage(1);
  }, [debouncedQuery, open, activeTab]);

  // fetch clients (list tab)
  useEffect(() => {
    if (!open) return;
    if (activeTab !== "list") return;

    if (!token) {
      setError("Нет токена авторизации");
      return;
    }

    const controller = new AbortController();

    const fetchClients = async () => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams();
        params.set("type", "client");
        params.set("page", String(page));
        params.set("per_page", String(PAGE_SIZE));

        if (debouncedQuery) {
          params.set("name", debouncedQuery);
        }

        const res = await fetch(`${API_URL}/api/contractors?${params.toString()}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(json?.message || `Ошибка загрузки клиентов (${res.status})`);
        }

        const list = Array.isArray(json?.data) ? json.data : [];
        const lp = Number(json?.last_page) || Number(json?.total_pages) || 1;

        setClients(
          list.map((c) => ({
            id: c.id,
            name: c.name || "—",
            phone: c.phone || "",
            sn: c.sn || "",
            address: c.address || "",
            city: c.city || "",
            org_type: c.org_type || "",
            raw: c,
          }))
        );

        setLastPage(lp);
      } catch (e) {
        if (e.name === "AbortError") return;
        setClients([]);
        setLastPage(1);
        setError(e.message || "Ошибка");
      } finally {
        setLoading(false);
      }
    };

    fetchClients();

    return () => controller.abort();
  }, [open, activeTab, token, page, debouncedQuery]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(lastPage, p + 1));

  const canCreate =
    form.sn.trim() &&
    form.name.trim() &&
    form.phone.trim() &&
    form.city.trim() &&
    form.address.trim() &&
    form.org_type;

  const createClient = async () => {
    if (!token) {
      setError("Нет токена авторизации");
      return;
    }
    if (!canCreate) return;

    setCreating(true);
    setError("");

    try {
      const payload = {
        sn: form.sn.trim(),
        type: "client",
        org_type: form.org_type,
        name: form.name.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        address: form.address.trim(),
      };

      const res = await fetch(`${API_URL}/api/contractors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json?.message || `Ошибка создания клиента (${res.status})`);
      }

      const created = json?.data || json;

      const newClient = {
        id: created?.id,
        name: created?.name || payload.name,
        phone: created?.phone || payload.phone,
        sn: created?.sn || payload.sn,
        address: created?.address || payload.address,
        city: created?.city || payload.city,
        org_type: created?.org_type || payload.org_type,
        raw: created,
      };

      setActiveTab("list");
      setClients((prev) => [newClient, ...prev]);
      setSelectedId(newClient.id);

      setForm({
        sn: "",
        type: "client",
        org_type: "individual",
        name: "",
        phone: "",
        city: "",
        address: "",
      });
    } catch (e) {
      setError(e.message || "Ошибка");
    } finally {
      setCreating(false);
    }
  };

  const handleConfirm = () => {
    if (activeTab === "list") {
      if (!selectedClient) return;
      onSelectClient?.(selectedClient);
      onClose?.();
      return;
    }

    createClient();
  };

  if (!open) return null;

  const modalJSX = (
    <div className="csm-overlay" onMouseDown={onClose}>
      <div className="csm-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="csm-header">
          <div className="csm-title">Выберите клиента</div>
          <button className="csm-close" onClick={onClose} aria-label="Закрыть" type="button">
            <X size={18} />
          </button>
        </div>

        <div className="csm-tabs">
          <button
            className={`csm-tab ${activeTab === "list" ? "active" : ""}`}
            onClick={() => setActiveTab("list")}
            type="button"
            disabled={creating}
          >
            Список клиентов
          </button>

          <button
            className={`csm-tab ${activeTab === "new" ? "active" : ""}`}
            onClick={() => setActiveTab("new")}
            type="button"
            disabled={creating}
          >
            Новый клиент
          </button>
        </div>

        {activeTab === "list" ? (
          <>
            <div className="csm-search">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск"
                className="csm-searchInput"
                disabled={loading || creating}
              />
            </div>

            <div className="csm-list">
              {loading ? (
                <div className="csm-empty">Загрузка...</div>
              ) : error ? (
                <div className="csm-empty">{error}</div>
              ) : clients.length === 0 ? (
                <div className="csm-empty">Ничего не найдено</div>
              ) : (
                clients.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`csm-item ${selectedId === c.id ? "selected" : ""}`}
                    onClick={() => setSelectedId(c.id)}
                    title={c.name}
                    disabled={creating}
                  >
                    {c.name}
                  </button>
                ))
              )}
            </div>

            <div className="csm-pagination">
              <button
                className="csm-pageBtn"
                type="button"
                onClick={goPrev}
                disabled={page <= 1 || loading || creating}
              >
                ‹
              </button>

              {pageNumbers.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`csm-pageNum ${page === n ? "active" : ""}`}
                  onClick={() => setPage(n)}
                  disabled={loading || creating}
                >
                  {n}
                </button>
              ))}

              <button
                className="csm-pageBtn"
                type="button"
                onClick={goNext}
                disabled={page >= lastPage || loading || creating}
              >
                ›
              </button>
            </div>
          </>
        ) : (
          <div className="csm-new">
            <div className="csm-newGrid">
              <div className="csm-newField">
                <div className="csm-newLabel">ИИН/БИН</div>
                <input
                  className="csm-newInput"
                  value={form.sn}
                  onChange={(e) => setForm((p) => ({ ...p, sn: e.target.value }))}
                  placeholder="Введите ИИН или БИН"
                  disabled={creating}
                />
              </div>

              <div className="csm-newField">
                <div className="csm-newLabel">Тип организации</div>
                <select
                  className="csm-newSelect"
                  value={form.org_type}
                  onChange={(e) => setForm((p) => ({ ...p, org_type: e.target.value }))}
                  disabled={creating}
                >
                  {Object.keys(typeMap).map((k) => (
                    <option key={k} value={k}>
                      {typeMap[k]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="csm-newField">
                <div className="csm-newLabel">Имя</div>
                <input
                  className="csm-newInput"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Введите имя клиента"
                  disabled={creating}
                />
              </div>

              <div className="csm-newField">
                <div className="csm-newLabel">Телефон</div>
                <input
                  className="csm-newInput"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="Введите телефон"
                  disabled={creating}
                />
              </div>

              <div className="csm-newField">
                <div className="csm-newLabel">Город</div>
                <input
                  className="csm-newInput"
                  value={form.city}
                  onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                  placeholder="Введите город"
                  disabled={creating}
                />
              </div>

              <div className="csm-newField">
                <div className="csm-newLabel">Адрес</div>
                <input
                  className="csm-newInput"
                  value={form.address}
                  onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                  placeholder="Введите адрес"
                  disabled={creating}
                />
              </div>
            </div>

            {error && <div className="csm-error">{error}</div>}
          </div>
        )}

        <button
          className="csm-confirm"
          type="button"
          onClick={handleConfirm}
          disabled={
            activeTab === "list"
              ? !selectedClient || loading || creating
              : !canCreate || creating
          }
        >
          {activeTab === "new" ? (creating ? "Создание..." : "Создать и выбрать") : "Выбрать"}
        </button>
      </div>
    </div>
  );

  return createPortal(modalJSX, document.body);
};

export default ClientSelectModal;
